/**
 * Hook que centraliza carregamento + polling do workspace pericial // ux-neutrality:allow doc-comment
 * (EW.F3.T09 + E8.F5.T07). Mantém K análises (uma por contestada) e // ux-neutrality:allow doc-comment
 * a tab ativa selecionada pelo usuário. Polling agrupado: 1 request // ux-neutrality:allow doc-comment
 * retorna estado de todas as K análises. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { AxiosInstance } from 'axios';
import { ptBR } from '@/i18n/pt-BR';
import {
  ChatError,
  type ChatErrorReason,
  type ChatMessageData,
  listChatMessages,
  postChatMessage,
} from '@/lib/case_chat';
import { listSignatures, type SignatureRecordData } from '@/lib/case_signatures';
import {
  getWorkspaceWithWarning,
  pollWorkspaceStatus,
  WorkspaceError,
  type CaseAnalysisData,
  type CaseAnalysisStatusData,
  type ProportionWarningData,
} from '@/lib/case_workspace';
import {
  adjustLr as apiAdjustLr,
  clearLr as apiClearLr,
  LrAdjustmentError,
  type LrAdjustmentErrorReason,
  type LrAdjustmentRequestData,
  type LrAdjustmentScope,
} from '@/lib/lr_adjustment';
import { setSystemMarkers as apiSetSystemMarkers } from '@/lib/system_markers';

const POLL_INTERVAL_MS = 2000;

function chatErrorMessage(reason: ChatErrorReason): string {
  if (reason === 'assistant_unavailable') return ptBR.workspaceCaso.chatErrorAssistantUnavailable;
  if (reason === 'assistant_timeout') return ptBR.workspaceCaso.chatErrorAssistantTimeout;
  if (reason === 'no_api_key') return ptBR.workspaceCaso.chatErrorNoApiKey;
  if (reason === 'network') return ptBR.workspaceCaso.chatErrorNetwork;
  return ptBR.workspaceCaso.chatErrorGeneric;
}

function lrAdjustErrorMessage(reason: LrAdjustmentErrorReason): string {
  if (reason === 'not_case_perito') return ptBR.workspaceCaso.lrAdjustErrorNotPerito;
  if (reason === 'unknown_criterion') return ptBR.workspaceCaso.lrAdjustErrorUnknownCriterion;
  if (reason === 'criterion_key_required' || reason === 'invalid_value') {
    return ptBR.workspaceCaso.lrAdjustErrorInvalidValue;
  }
  if (reason === 'network') return ptBR.workspaceCaso.lrAdjustErrorNetwork;
  return ptBR.workspaceCaso.lrAdjustErrorGeneric;
}

export interface WorkspaceData {
  analyses: CaseAnalysisData[];
  activeContestedId: string | null;
  setActiveContestedId: (id: string) => void;
  activeAnalysis: CaseAnalysisData | null;
  signatures: SignatureRecordData[];
  messages: ChatMessageData[];
  proportionWarning: ProportionWarningData | null;
  loadError: string | null;
  chatError: string | null;
  sending: boolean;
  sendChat: (content: string, criterionId: string | null) => Promise<void>;
  // E33.F2/ADR-0044: ajuste manual de LR pelo perito na mesa.
  adjusting: boolean;
  adjustError: string | null;
  adjustLr: (analysisId: string, request: LrAdjustmentRequestData) => Promise<void>;
  clearLr: (
    analysisId: string,
    scope: LrAdjustmentScope,
    criterionKey?: string | null,
  ) => Promise<void>;
  // E38.F2: visibilidade persistida das marcações do sistema por análise.
  setSystemMarkers: (analysisId: string, show: boolean) => Promise<void>;
}

export function useWorkspaceData(
  client: AxiosInstance,
  caseId: string,
  navigate: NavigateFunction,
): WorkspaceData {
  const [analyses, setAnalyses] = useState<CaseAnalysisData[]>([]);
  const [activeContestedId, setActiveContestedId] = useState<string | null>(null);
  const [signatures, setSignatures] = useState<SignatureRecordData[]>([]);
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [proportionWarning, setProportionWarning] = useState<ProportionWarningData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  // Seletor: a análise ativa é a com contested_signature_id selecionado,
  // ou a primeira da lista se nada foi selecionado ainda.
  const activeAnalysis = useMemo<CaseAnalysisData | null>(() => {
    const first = analyses[0];
    if (!first) return null;
    if (activeContestedId === null) return first;
    return analyses.find((a) => a.contested_signature_id === activeContestedId) ?? first;
  }, [analyses, activeContestedId]);

  const refresh = useCallback(async () => {
    try {
      const [workspace, sigs, msgs] = await Promise.all([
        getWorkspaceWithWarning(client, caseId),
        listSignatures(client, caseId),
        listChatMessages(client, caseId),
      ]);
      const list = workspace.analyses;
      setAnalyses(list);
      setProportionWarning(workspace.proportionWarning);
      setSignatures(sigs);
      setMessages(msgs);
      setLoadError(null);
      // Inicializa a tab ativa na primeira contestada caso ainda não tenha sido escolhida.
      setActiveContestedId((curr) => {
        if (curr !== null && list.some((a) => a.contested_signature_id === curr)) {
          return curr;
        }
        const first = list[0];
        return first ? first.contested_signature_id : null;
      });
    } catch (err: unknown) {
      if (err instanceof WorkspaceError && err.reason === 'not_ready') {
        navigate(`/casos/${caseId}/wizard`);
        return;
      }
      setLoadError(ptBR.workspaceCaso.errorLoadGeneric);
    }
  }, [caseId, client, navigate]);

  const refreshAnalysesOnly = useCallback(async () => {
    try {
      const workspace = await getWorkspaceWithWarning(client, caseId);
      setAnalyses(workspace.analyses);
      setProportionWarning(workspace.proportionWarning);
    } catch {
      // silencioso: refresh oportunista após chat, não deve sobrescrever erro de load
    }
  }, [caseId, client]);

  // Ref espelhando `analyses` — fonte de verdade síncrona para
  // computações que não podem depender de fechamentos do useState
  // (E8.F6.T04 / F-E8.F5-05: bug exposto por teste — o updater de
  // setState não garante execução síncrona, então decidir
  // `anyJustCompleted` dentro do updater e ler o resultado na linha
  // seguinte era unreliable).
  const analysesRef = useRef<CaseAnalysisData[]>([]);
  useEffect(() => {
    analysesRef.current = analyses;
  }, [analyses]);

  // E8.F5.C02: polling enxuto — usa `pollWorkspaceStatus` (`/workspace/status`) // ux-neutrality:allow doc-comment
  // em vez de `getWorkspace` (`/workspace`). A rota pesada dispara // ux-neutrality:allow doc-comment
  // `dispatch_case_analyses` que, antes do C01 backend, re-agendava // ux-neutrality:allow doc-comment
  // BackgroundTask para análises ainda em pending → trabalho duplicado. // ux-neutrality:allow doc-comment
  // Agora dispatch é idempotente (C01) e o polling não dispara dispatch. // ux-neutrality:allow doc-comment
  const pollStatuses = useCallback(async () => {
    let statuses: CaseAnalysisStatusData[];
    try {
      statuses = await pollWorkspaceStatus(client, caseId);
    } catch {
      return; // 404 transitório (entre mounts) é silencioso
    }
    const statusById = new Map(statuses.map((s) => [s.analysis_id, s] as const));
    // Detecta transição terminal a partir do snapshot atual (ref síncrono).
    const snapshot = analysesRef.current;
    const anyJustCompleted = snapshot.some((analysis) => {
      const update = statusById.get(analysis.id);
      return (
        update !== undefined &&
        (analysis.status === 'pending' || analysis.status === 'running') &&
        (update.status === 'completed' || update.status === 'failed')
      );
    });
    setAnalyses((curr) => {
      let mutated = false;
      const next = curr.map((analysis) => {
        const update = statusById.get(analysis.id);
        if (!update) return analysis;
        if (update.status === analysis.status && update.completed_at === analysis.completed_at) {
          return analysis;
        }
        mutated = true;
        return {
          ...analysis,
          status: update.status,
          completed_at: update.completed_at,
        };
      });
      return mutated ? next : curr;
    });
    // Quando uma análise transiciona para terminal, recarrega o objeto // ux-neutrality:allow doc-comment
    // completo (patterns + composite_lr + agent_summary) — único momento // ux-neutrality:allow doc-comment
    // em que precisamos da rota pesada durante o polling. // ux-neutrality:allow doc-comment
    if (anyJustCompleted) {
      void refreshAnalysesOnly();
    }
  }, [caseId, client, refreshAnalysesOnly]);

  useEffect(() => {
    if (!caseId) return;
    void refresh();
  }, [caseId, refresh]);

  // Polling: enquanto qualquer análise estiver pending/running, polla via
  // `/workspace/status` (1 request enxuta retorna estado de todas K análises).
  useEffect(() => {
    if (analyses.length === 0) return;
    const anyRunning = analyses.some((a) => a.status === 'pending' || a.status === 'running');
    if (!anyRunning) {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    if (pollTimer.current !== null) return;
    pollTimer.current = window.setInterval(() => {
      void pollStatuses();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [analyses, pollStatuses]);

  const sendChat = useCallback(
    async (content: string, criterionId: string | null): Promise<void> => {
      setSending(true);
      setChatError(null);
      try {
        const assistantMsg = await postChatMessage(client, caseId, {
          content,
          criterion_id: criterionId,
          contested_signature_id: activeContestedId,
        });
        const userMsg: ChatMessageData = {
          id: `local-${Date.now()}`,
          case_id: caseId,
          role: 'user',
          content,
          criterion_id: criterionId,
          created_at: new Date().toISOString(),
        };
        setMessages((curr) => [...curr, userMsg, assistantMsg]);
        // Após o chat, recarrega só o estado das análises (sem mexer em
        // signatures/messages para não atropelar a atualização otimista).
        void refreshAnalysesOnly();
      } catch (err: unknown) {
        const reason: ChatErrorReason = err instanceof ChatError ? err.reason : 'unknown';
        setChatError(chatErrorMessage(reason));
      } finally {
        setSending(false);
      }
    },
    [activeContestedId, caseId, client, refreshAnalysesOnly],
  );

  // E33.F2/ADR-0044: ajuste manual de LR pelo perito. Após PATCH/DELETE,
  // recarrega o workspace — o backend devolve o LR EFETIVO e TODOS os derivados
  // (direção/força/banda/status/veredito) recalculados, então a mesa, o medidor
  // e a matriz atualizam ao vivo de uma fonte única. Re-lança o erro para o
  // chamador (o controle mantém o valor digitado em caso de falha).
  const adjustLr = useCallback(
    async (analysisId: string, request: LrAdjustmentRequestData): Promise<void> => {
      setAdjusting(true);
      setAdjustError(null);
      try {
        await apiAdjustLr(client, caseId, analysisId, request);
        await refreshAnalysesOnly();
      } catch (err: unknown) {
        const reason: LrAdjustmentErrorReason =
          err instanceof LrAdjustmentError ? err.reason : 'unknown';
        setAdjustError(lrAdjustErrorMessage(reason));
        throw err;
      } finally {
        setAdjusting(false);
      }
    },
    [caseId, client, refreshAnalysesOnly],
  );

  const clearLr = useCallback(
    async (
      analysisId: string,
      scope: LrAdjustmentScope,
      criterionKey?: string | null,
    ): Promise<void> => {
      setAdjusting(true);
      setAdjustError(null);
      try {
        await apiClearLr(client, caseId, analysisId, scope, criterionKey);
        await refreshAnalysesOnly();
      } catch (err: unknown) {
        const reason: LrAdjustmentErrorReason =
          err instanceof LrAdjustmentError ? err.reason : 'unknown';
        setAdjustError(lrAdjustErrorMessage(reason));
        throw err;
      } finally {
        setAdjusting(false);
      }
    },
    [caseId, client, refreshAnalysesOnly],
  );

  // E38.F2: alterna a visibilidade das marcações do sistema. Otimista (o overlay
  // some/aparece na hora, sem esperar a rede); persiste por análise e, em falha,
  // reverte ao estado do servidor. Sem `adjusting`/erro de banner: é um toggle
  // de apresentação, a reversão visual já é o feedback.
  const setSystemMarkers = useCallback(
    async (analysisId: string, show: boolean): Promise<void> => {
      setAnalyses((curr) =>
        curr.map((a) => (a.id === analysisId ? { ...a, show_system_markers: show } : a)),
      );
      try {
        await apiSetSystemMarkers(client, caseId, analysisId, show);
      } catch {
        await refreshAnalysesOnly();
      }
    },
    [caseId, client, refreshAnalysesOnly],
  );

  return {
    analyses,
    activeContestedId,
    setActiveContestedId,
    activeAnalysis,
    signatures,
    messages,
    proportionWarning,
    loadError,
    chatError,
    sending,
    sendChat,
    adjusting,
    adjustError,
    adjustLr,
    clearLr,
    setSystemMarkers,
  };
}
